import type { ReactNode } from 'react';
import { AlertTriangle, Inbox, Loader2, WifiOff } from 'lucide-react';
import { Button } from './Button';

export function Spinner({ label = 'Cargando…' }: { label?: string }) {
  return (
    <div role="status" className="flex flex-col items-center justify-center gap-3 py-16 text-[var(--text-muted)]">
      <Loader2 className="size-7 animate-spin" aria-hidden />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function Skeleton({ className = 'h-20' }: { className?: string }) {
  return <div className={`animate-pulse rounded-2xl bg-black/5 dark:bg-white/10 ${className}`} aria-hidden />;
}

export function EmptyState({ title, description, action, icon }: { title: string; description?: string; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="flex flex-col items-center text-center gap-3 py-14 px-6">
      <div className="size-14 rounded-full bg-black/5 dark:bg-white/10 flex items-center justify-center text-[var(--text-muted)]">
        {icon ?? <Inbox className="size-6" aria-hidden />}
      </div>
      <h3 className="font-semibold text-base">{title}</h3>
      {description && <p className="text-sm text-[var(--text-muted)] max-w-sm">{description}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ message, onRetry, offline }: { message: string; onRetry?: () => void; offline?: boolean }) {
  return (
    <div role="alert" className="flex flex-col items-center text-center gap-3 py-14 px-6">
      <div className="size-14 rounded-full bg-brand-red/10 flex items-center justify-center text-brand-red">
        {offline ? <WifiOff className="size-6" aria-hidden /> : <AlertTriangle className="size-6" aria-hidden />}
      </div>
      <p className="text-sm max-w-sm">{message}</p>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Reintentar
        </Button>
      )}
    </div>
  );
}

export function Notice({ tone = 'info', children }: { tone?: 'info' | 'warning' | 'danger'; children: ReactNode }) {
  const map = {
    info: 'bg-sky-50 text-sky-900 border-sky-200 dark:bg-sky-500/10 dark:text-sky-200 dark:border-sky-500/30',
    warning: 'bg-amber-50 text-amber-900 border-amber-200 dark:bg-amber-500/10 dark:text-amber-200 dark:border-amber-500/30',
    danger: 'bg-red-50 text-red-900 border-red-200 dark:bg-red-500/10 dark:text-red-200 dark:border-red-500/30',
  } as const;
  return <div className={`rounded-2xl border px-4 py-3 text-sm ${map[tone]}`}>{children}</div>;
}
